/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect } from "react";
import { When } from "react-if";
import {
  Button,
  Description,
  Label,
  Spacer,
  Switch,
  theme,
  FlexContainer,
} from "@nordcloud/gnui";
import { Provider } from "~/generated/graphql";
import {
  Fieldset,
  InputLoader,
  SidebarWithMultiSelect,
  FiltersFooter,
} from "~/components";
import { isNotEmpty, handleError } from "~/tools";
import { AppEnvSelectorPaginated } from "./AppEnvSelectorPaginated";
import { ESTATE_FILTER_LABEL } from "./constants";
import {
  UnassignedRecordsDateRange,
  ScannedRecordsDateRange,
} from "./DateRange";
import { CostInput } from "./Filters/CostInput";
import {
  useDataOptions,
  useHandleEstateFilters,
  DisabledFilters,
} from "./hooks";
import { MultiMetadataInput } from "./MultiMetadataInput/MultiMetadataInput";
import { MultiTagsInput } from "./MultiTagsInput/MultiTagsInput";
import { NumberRange } from "./NumberRange";
import { ProviderMultiSelect } from "./ProviderMultiSelect";
import ResourceLifeSpanCheckbox from "./ResourceLifeSpanCheckbox";
import { FiltersProp } from "./types";

export const disabledFilterFields = new Map<string, string[]>();
disabledFilterFields.set(DisabledFilters.EXTERNAL, ["externalIntegration"]);
disabledFilterFields.set(DisabledFilters.ENRICHMENT, [
  "enrichment",
  "enrichmentIntegrationId",
]);
disabledFilterFields.set(DisabledFilters.APPS, ["application", "environment"]);

type Props = {
  filters: FiltersProp;
  disabledFilters?: DisabledFilters[];
  setFilters: (filter: FiltersProp) => void;
  isAccount?: boolean;
  onApply: () => void;
};

// TODO: reduce size
// eslint-disable-next-line max-lines-per-function
export function EstateFilters({
  filters,
  setFilters,
  isAccount = false,
  onApply,
  disabledFilters = [],
}: Props) {
  const filtersData = useDataOptions(filters);

  const {
    accountsOptions,
    regionOptions,
    resourceTypes,
    categories,
    cloudWastePolicies,
    externalIntegrations,
    error,
    loadingCategories,
    loadingCloudWaste,
    loadingExternalIntegrations,
    loadingResourceTypes,
    loadingAccounts,
  } = filtersData;

  const {
    commit,
    reset,
    selectAppsAndEnvs,
    toggleProvider,
    isRegionEnabled,
    showEstateUnassigned,
    showExternalIntegration,
    showEnrichment,
    showApps,
    hasEmptyFilters,
    isApplyButtonDisabled,
  } = useHandleEstateFilters({
    filters,
    setFilters,
    onApply,
    disabledFilters,
  });

  useEffect(() => {
    if (error != null) {
      handleError(error);
    }
  }, [error]);

  const selectedAccounts = (filters?.account ?? []).map((account) =>
    account.toString()
  );

  return (
    <>
      <When condition={!isAccount}>
        <Fieldset>
          <ProviderMultiSelect
            value={filters.provider ?? []}
            onSelect={toggleProvider}
          />
        </Fieldset>
      </When>
      <Fieldset>
        <Label name="Monthly Cost" />
        <CostInput filters={filters} updateFilters={setFilters} />
      </Fieldset>
      <Fieldset>
        <Label
          name="Discovery Time"
          css={{ margin: theme.spacing.spacing00 }}
        />
        <Description>
          The time range when the resource was discovered
        </Description>
        <ScannedRecordsDateRange filters={filters} setFilters={setFilters} />
      </Fieldset>
      <Fieldset>
        <Label
          name="Resource Life Span"
          css={{ margin: theme.spacing.spacing00 }}
        />
        <Description>
          Specify the life span of the resource activity
        </Description>
        <FlexContainer alignItems="flex-start">
          <NumberRange filters={filters} setFilters={setFilters} />
          <span
            css={{
              paddingTop: "10px",
            }}
          >
            days
          </span>
        </FlexContainer>
        <ResourceLifeSpanCheckbox filters={filters} setFilters={setFilters} />
      </Fieldset>
      <Fieldset>
        <Label name="Estate Records" />
        <When condition={showApps && showEstateUnassigned}>
          <Switch
            checked={filters.unassigned}
            labelText={ESTATE_FILTER_LABEL.unassigned}
            position="right"
            id="switch-unassigned"
            onChange={() =>
              setFilters({
                ...filters,
                unassigned: filters.unassigned ? undefined : true,
                region: undefined,
                assignmentUpdatedFrom: undefined,
                assignmentUpdatedTo: undefined,
              })
            }
          />
          <When condition={filters.unassigned}>
            <Spacer height={theme.spacing.spacing04} />
            <UnassignedRecordsDateRange
              filters={filters}
              setFilters={setFilters}
            />
          </When>
        </When>
        <Spacer height={theme.spacing.spacing04} />
        <Switch
          checked={filters.waste}
          labelText={ESTATE_FILTER_LABEL.waste}
          position="right"
          id="switch-cloud-waste"
          onChange={() =>
            setFilters({
              ...filters,
              waste: filters.waste ? undefined : true,
              wastePolicy: filters.waste ? undefined : filters.wastePolicy,
            })
          }
        />
        <When condition={filters.waste}>
          <Spacer height={theme.spacing.spacing04} />
          <InputLoader loading={loadingCloudWaste}>
            <SidebarWithMultiSelect
              name="Savings Suggestions Policy"
              pluralName="Savings Suggestions Policies"
              options={cloudWastePolicies}
              selectedOptions={filters?.wastePolicy ?? []}
              onClear={() => setFilters({ ...filters, wastePolicy: undefined })}
              onMultiSelect={(wastePolicy) =>
                setFilters({
                  ...filters,
                  wastePolicy,
                })
              }
            />
          </InputLoader>
        </When>
        <Spacer height={theme.spacing.spacing04} />
        <Switch
          checked={filters.co2eWaste}
          labelText={ESTATE_FILTER_LABEL.co2eWaste}
          position="right"
          id="switch-co2e-waste"
          onChange={() =>
            setFilters({
              ...filters,
              co2eWaste: filters.co2eWaste ? undefined : true,
            })
          }
        />
        <InputLoader
          loading={loadingExternalIntegrations}
          css={{ marginTop: theme.spacing.spacing03, height: "60px" }}
        >
          <When condition={isNotEmpty(externalIntegrations)}>
            <When condition={showEnrichment}>
              <Spacer height={theme.spacing.spacing04} />
              <Switch
                checked={filters.enrichment}
                labelText={ESTATE_FILTER_LABEL.enrichment}
                position="right"
                id="switch-enrichment"
                onChange={() =>
                  setFilters({
                    ...filters,
                    enrichment: filters.enrichment ? undefined : true,
                    enrichmentIntegrationId: filters.enrichment
                      ? undefined
                      : filters.enrichmentIntegrationId,
                  })
                }
              />
              <When condition={filters.enrichment}>
                <Spacer height={theme.spacing.spacing04} />
                <InputLoader loading={loadingExternalIntegrations}>
                  <SidebarWithMultiSelect
                    name="External Integration"
                    options={externalIntegrations}
                    selectedOptions={filters?.enrichmentIntegrationId ?? []}
                    onClear={() =>
                      setFilters({
                        ...filters,
                        enrichmentIntegrationId: undefined,
                      })
                    }
                    onMultiSelect={(enrichmentIntegrationId) =>
                      setFilters({
                        ...filters,
                        enrichmentIntegrationId,
                      })
                    }
                  />
                </InputLoader>
              </When>
            </When>

            <When condition={showExternalIntegration}>
              <Spacer height={theme.spacing.spacing04} />
              <Switch
                checked={filters.provider?.includes(Provider.External)}
                labelText={ESTATE_FILTER_LABEL.external}
                position="right"
                id="switch-external"
                onChange={() => toggleProvider(Provider.External)}
              />
              <When condition={filters.provider?.includes(Provider.External)}>
                <Spacer height={theme.spacing.spacing04} />
                <SidebarWithMultiSelect
                  name="External Integration"
                  options={externalIntegrations}
                  selectedOptions={filters?.externalIntegration ?? []}
                  onClear={() =>
                    setFilters({ ...filters, externalIntegration: undefined })
                  }
                  onMultiSelect={(externalIntegration) =>
                    setFilters({
                      ...filters,
                      externalIntegration,
                    })
                  }
                />
              </When>
            </When>
          </When>
        </InputLoader>
      </Fieldset>
      <When condition={!isAccount}>
        <Fieldset>
          <Label name="Cloud Accounts and Region" />
          <SidebarWithMultiSelect
            hasSubtitle
            hasIcon
            name="Account"
            options={accountsOptions}
            selectedOptions={selectedAccounts}
            showLoader={loadingAccounts}
            onClear={() => setFilters({ ...filters, account: undefined })}
            onMultiSelect={(account) =>
              setFilters({
                ...filters,
                account,
              })
            }
          />
        </Fieldset>
        <Fieldset>
          <SidebarWithMultiSelect
            name="Region"
            options={regionOptions}
            disabled={!isRegionEnabled}
            title={isRegionEnabled ? "Select Provider to enable" : ""}
            selectedOptions={filters?.region ?? []}
            onClear={() => setFilters({ ...filters, region: undefined })}
            onMultiSelect={(region) =>
              setFilters({
                ...filters,
                region,
              })
            }
          />
        </Fieldset>
      </When>
      <Fieldset>
        <InputLoader loading={loadingCategories}>
          <Label name="Resource Categories and Resource Types" />
          <SidebarWithMultiSelect
            hasIcon
            name="Resource Category"
            pluralName="Categories"
            options={categories}
            selectedOptions={filters?.category ?? []}
            onClear={() => setFilters({ ...filters, category: undefined })}
            onMultiSelect={(category) =>
              setFilters({
                ...filters,
                category,
              })
            }
          />
        </InputLoader>

        <Spacer height={theme.spacing.spacing04} />

        <InputLoader loading={loadingResourceTypes}>
          <SidebarWithMultiSelect
            hasIcon
            name="Resource Type"
            options={resourceTypes}
            selectedOptions={filters?.typeSubtype ?? []}
            onClear={() => setFilters({ ...filters, typeSubtype: undefined })}
            onMultiSelect={(typeSubtype) =>
              setFilters({
                ...filters,
                typeSubtype,
              })
            }
          />
        </InputLoader>
      </Fieldset>
      <When condition={!filters.unassigned && showApps}>
        <Fieldset>
          <Label name="Applications and Environments" />
          <AppEnvSelectorPaginated
            selectedApplications={filters.application ?? []}
            selectedEnvironments={filters.environment ?? []}
            onChange={selectAppsAndEnvs}
            onClear={() =>
              setFilters({
                ...filters,
                application: undefined,
                environment: undefined,
              })
            }
          />
        </Fieldset>
      </When>
      <Fieldset>
        <Label name="Tag Name and Tag Value" />
        <MultiTagsInput
          inputTags={filters.tags}
          onChange={(tags) =>
            setFilters({
              ...filters,
              tags,
            })
          }
        />
      </Fieldset>
      <Fieldset>
        <Label name="Metadata Key and Value" />
        <MultiMetadataInput
          inputMetadata={filters.metadata}
          onChange={(metadata) =>
            setFilters({
              ...filters,
              metadata,
            })
          }
        />
      </Fieldset>
      <Spacer height={theme.spacing.spacing04} />
      <FiltersFooter>
        <Button
          disabled={isApplyButtonDisabled}
          mr={theme.spacing.spacing02}
          onClick={commit}
        >
          Apply filters
        </Button>
        <Button severity="low" disabled={hasEmptyFilters} onClick={reset}>
          Clear filters &amp; reload
        </Button>
      </FiltersFooter>
    </>
  );
}

export { DisabledFilters } from "./hooks/useHandleEstateFilters";
